import React, { useCallback, useEffect, useState } from 'react'
import { useDeviceType } from '~/app/utils/useDeviceType'
import { cn, RectangleButton, BottomSheet } from '@yanolja-rnd/nol-design-system'
import { IcDone } from '@yanolja-rnd/nol-icons'
import { DomesticCartDayuseTimeLine } from '~/api/routes/domestic/cart/controller/type'
import { CartPageLog } from '~/app/kinesis/protobuf/clientlogs/order-misc-common-txtpb'
import Impression from '~/app/components/common/Impression'
import { zIndexExtra } from '~/app/components/styles/units'

interface DayUseTimelinesBottomSheetProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  timeLines: DomesticCartDayuseTimeLine[]
  initialTimeLine: DomesticCartDayuseTimeLine | undefined
  onChangeTimeLine: (timeLine: DomesticCartDayuseTimeLine) => void
}

const TimeLineText = ({
  timeLine,
  isActive,
  onClick,
}: {
  timeLine: DomesticCartDayuseTimeLine
  isActive: boolean
  onClick: () => void
}) => {
  return (
    <div
      className={cn(
        'flex h-48 cursor-pointer gap-10 px-16 py-4',
        isActive && 'rounded-8 bg-fill-neutral-weak'
      )}
      onClick={onClick}
    >
      <div
        className={cn(
          'flex h-full w-full items-center justify-start',
          isActive ? 'typography-subtitle-16-bold' : 'typography-subtitle-16-regular'
        )}
      >
        {timeLine.timeRangeText}
      </div>
      {isActive && (
        <div className="flex items-center">
          <IcDone className="size-24" />
        </div>
      )}
    </div>
  )
}

const DayUseTimelinesBottomSheet: React.FC<DayUseTimelinesBottomSheetProps> = ({
  isOpen,
  onClose,
  onConfirm,
  timeLines,
  initialTimeLine,
  onChangeTimeLine,
}) => {
  const [selectedTimeLine, setSelectedTimeLine] = useState<DomesticCartDayuseTimeLine | undefined>(
    initialTimeLine
  )
  const isActive = (timeLine: DomesticCartDayuseTimeLine) => {
    return selectedTimeLine?.checkInAt === timeLine.checkInAt
  }

  const deviceType = useDeviceType()

  useEffect(() => {
    setSelectedTimeLine(initialTimeLine)
  }, [initialTimeLine])

  const changeButtonClick = useCallback(async () => {
    if (selectedTimeLine) {
      onChangeTimeLine(selectedTimeLine)
    }
  }, [onChangeTimeLine, selectedTimeLine])

  const trackImpression = async () => {
    await CartPageLog.PopupSectionName.impression({
      sectionName: 'CART_POPUP_TYPE_CHANGE_USE_TIME',
    })
  }

  const onCancelClose = useCallback(() => {
    onClose()
    setSelectedTimeLine(initialTimeLine)
  }, [onClose, setSelectedTimeLine, initialTimeLine])

  return (
    <Impression onImpressed={trackImpression}>
      <BottomSheet
        isOpen={isOpen}
        os={deviceType}
        height="half"
        header="대실 시간 변경"
        footer={
          <RectangleButton
            theme="filled_primary"
            weight="bold"
            fullWidth
            size="large"
            onClick={changeButtonClick}
          >
            변경하기
          </RectangleButton>
        }
        onClose={onCancelClose}
        onBackdropClick={onCancelClose}
        dialogOptions={{
          onBackdropClick: () => {
            deviceType === 'etc' && onCancelClose()
          },
        }}
        // 모바일 뷰에서 바텀시트 닫힐 때 실행
        onCloseEnd={() => onConfirm()}
        // PC 뷰에서 바텀시트 닫힐 때 실행
        onAnimationEnd={() => {
          !isOpen && onConfirm()
        }}
        zIndex={zIndexExtra}
      >
        <div className={cn('grid gap-4 px-16 py-8', 'pf-pc:px-0 pf-pc:py-0')}>
          {timeLines.map((timeLine, index) => (
            <TimeLineText
              key={index}
              timeLine={timeLine}
              isActive={isActive(timeLine)}
              onClick={() => setSelectedTimeLine(timeLine)}
            />
          ))}
        </div>
      </BottomSheet>
    </Impression>
  )
}

export default DayUseTimelinesBottomSheet
